import type { DealStatusDealerFeeEntry } from "@credit-acceptance/step6-bff-client";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { formatSignedCurrency } from "../../../../lib/money";
import AuditTable, { type AuditTableColumn } from "../../../audit/AuditTable";

interface DealFeesModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fees: DealStatusDealerFeeEntry[];
  total: number;
}

const amountFormatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Breakdown of the Deal Fees deduction, opened from the "Deal Fees" link in DealStatus
function DealFeesModal({ open, onOpenChange, fees, total }: DealFeesModalProperties) {
  const { t } = useTranslation();

  const columns: Array<AuditTableColumn<DealStatusDealerFeeEntry>> = [
    { key: "name", header: t("dealStatus.dealFeesModal.feeName"), render: (fee) => fee.feeName ?? "—" },
    {
      key: "amount",
      header: t("dealStatus.dealFeesModal.amount"),
      render: (fee) => amountFormatter.format(fee.feeAmount ?? 0),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="gap-(--spacing-0) p-(--spacing-0) overflow-hidden bg-white sm:max-w-lg"
      >
        <div className="bg-primary text-primary-foreground px-(--spacing-4) py-(--spacing-2) flex items-center justify-between">
          <DialogTitle className="text-primary-foreground text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height) font-(--font-weight-semibold)">
            {t("dealStatus.dealFeesModal.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("dealStatus.dealFeesModal.description")}</DialogDescription>
          <DialogClose className="text-primary-foreground/80 hover:text-primary-foreground p-(--spacing-0-5) transition-colors">
            <X className="size-4.5" aria-hidden="true" />
            <span className="sr-only">{t("dealStatus.dealFeesModal.close")}</span>
          </DialogClose>
        </div>

        <div className="gap-(--spacing-3) p-(--spacing-6) flex flex-col">
          <div className="gap-(--spacing-12) text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height) font-(--font-weight-semibold) flex">
            <span>{t("dealStatus.dealFeesModal.title")}</span>
            <span>{formatSignedCurrency(total)}</span>
          </div>

          <AuditTable caption={t("dealStatus.dealFeesModal.caption")} columns={columns} rows={fees} />
        </div>

        <DialogFooter className="px-(--spacing-4) py-(--spacing-2) justify-center border-t sm:justify-center">
          <DialogClose asChild>
            <Button type="button" size="xs">
              {t("dealStatus.dealFeesModal.cancel")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DealFeesModal;
